import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api/axiosConfig';
import AiProviderCard from '../components/AiProviderCard';
import { AdminAnalyticsDto } from '../types';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

const Dashboard: React.FC = () => {
    const [analytics, setAnalytics] = useState<AdminAnalyticsDto | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                const response = await api.get<AdminAnalyticsDto>('/admin/analytics');
                setAnalytics(response.data);
            } catch (error) {
                console.error('Error fetching analytics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    if (loading) {
        return (
            <Container className="d-flex justify-content-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    if (!analytics) return null;

    const throughputData = Object.entries(analytics.dailyThroughput).map(([date, count]) => ({ date, count }));
    const dailyDiagnosisData = Object.entries(analytics.dailyDiagnoses || {}).map(([date, count]) => ({ date, count }));
    const doctorData = Object.entries(analytics.imagesByDoctor).map(([name, count]) => ({ name, count }));
    const diagnosisData = [
        { name: 'Успешно', value: analytics.totalDiagnoses - analytics.errorDiagnoses },
        { name: 'Ошибка', value: analytics.errorDiagnoses },
        { name: 'Без диагноза', value: analytics.detailedImagesWithoutDiagnosis }
    ];

    return (
        <Container className="mt-4">
            <h2 className="mb-4">Аналитика</h2>

            <AiProviderCard />
            
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Всего Пациентов</Card.Title>
                            <h3 className="text-primary">{analytics.totalPatients}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Детальные Снимки</Card.Title>
                            <h3 className="text-success">{analytics.totalDetailedImages}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Всего Диагнозов</Card.Title>
                            <h3 className="text-info">{analytics.totalDiagnoses}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Ручные Контуры</Card.Title>
                            <h3 className="text-warning">{analytics.manualContourEngagedCount}</h3>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={6} className="mb-4">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title>Загрузка изображений (по дням)</Card.Title>
                            <div style={{ height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={throughputData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="count" fill="#8884d8" name="Загружено" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6} className="mb-4">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title>Диагностика (по дням)</Card.Title>
                            <div style={{ height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dailyDiagnosisData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="count" fill="#82ca9d" name="Диагнозы" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={8} className="mb-4">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title>Активность Врачей</Card.Title>
                            <div style={{ height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={doctorData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="count" fill="#00C49F" name="Снимков" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4} className="mb-4">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title>Статус Диагностики</Card.Title>
                            <div style={{ height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={diagnosisData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                            label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {diagnosisData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Dashboard;